import type { UserSummary } from '@susdle/shared';
import type { DiscordSdkPort } from './DiscordSdkPort.js';
import type { IdentityProvider } from './IdentityProvider.js';
import { discordAvatarUrl } from './discordAvatar.js';

/**
 * Production identity provider for Discord Activities. Constructor takes a
 * `DiscordSdkPort` (Task 7 — minimal SDK surface) and is side-effect-free.
 *
 * Per architecture §5.1: identity sourcing uses the standard Discord
 * Activity OAuth flow (`authorize({ scope: ['identify', 'rpc.voice.read'] })` → server token
 * exchange → `authenticate(token)`). The flow lives in
 * `realDiscordSdkAdapter.getCurrentUser` (Task 11) — this provider is just
 * the abstraction layer that maps the resulting `DiscordParticipant` to
 * `UserSummary`. Tests inject `FakeDiscordSdk` and never touch the real
 * OAuth machinery.
 *
 * `getRoomParticipants` is intentionally NOT implemented on this class —
 * the interface declares it optional and Phase 1 has no consumer (Colyseus
 * state is the lobby population). Add when a real consumer surfaces.
 */
export class DiscordIdentityProvider implements IdentityProvider {
  private readyPromise: Promise<void> | null = null;

  constructor(private readonly sdk: DiscordSdkPort) {}

  async getCurrentUser(): Promise<UserSummary> {
    await this.ensureReady();
    const user = await this.sdk.getCurrentUser();
    return {
      id: user.id,
      name: user.global_name ?? user.username,
      avatarUrl: discordAvatarUrl({
        userId: user.id,
        avatarHash: user.avatar,
        discriminator: user.discriminator,
      }),
    };
  }

  private ensureReady(): Promise<void> {
    // Cache the readiness promise so concurrent `getCurrentUser` calls share
    // one SDK ready() invocation. On rejection, clear the cache so a retry
    // can re-attempt — caching a rejected promise would permanently poison
    // the provider after one transient SDK failure.
    if (this.readyPromise === null) {
      this.readyPromise = this.sdk.ready().catch((err: unknown) => {
        this.readyPromise = null;
        throw err;
      });
    }
    return this.readyPromise;
  }
}
